import type {AddFormType} from 'context/guest';
import {FormField, GUEST_FORM_NAMES} from '@chekin/core';
import i18n from 'i18next';
import {FormFieldsMapper} from '../types';

type FieldGroup = {
  name: string;
  title: string;
  fields: FormField[];
  isCustom: boolean;
  order: number;
};

const getGroupTitle = (field: FormField) =>
  field.is_custom_group
    ? field.group_name
    : i18n.t(`default_form_group.${field.group_name}`);

const sortFieldsByOrder = (fields: FormField[]) =>
  fields.sort((a, b) => a.order - b.order);

const isActive = (field: FormField) => field.is_active;
const isOnlyForLeader = (isLeaderGuest: boolean) => (field: FormField) =>
  field.is_leaders_field ? field.is_required || isLeaderGuest : true;
const isDefaultInFieldsMapper =
  (formFieldsMapper: FormFieldsMapper) => (field: FormField) =>
    field.is_custom ? true : Boolean(formFieldsMapper[field.name as GUEST_FORM_NAMES]);

const fieldsFilter =
  (formFieldsMapper: FormFieldsMapper, isLeaderGuest: boolean) => (field: FormField) => {
    return (
      isActive(field) &&
      isOnlyForLeader(isLeaderGuest)(field) &&
      isDefaultInFieldsMapper(formFieldsMapper)(field)
    );
  };

export const groupByGroupName = (
  formFields: AddFormType,
  formFieldsMapper: FormFieldsMapper,
  isLeaderGuest: boolean,
) => {
  const groups = sortFieldsByOrder(Object.values(formFields))
    .filter(fieldsFilter(formFieldsMapper, isLeaderGuest))
    .reduce((groupList, field) => {
      const group = groupList.find(({name}) => name === field.group_name);
      if (group) {
        group.fields.push(field);
      } else {
        groupList.push({
          name: field.group_name,
          title: getGroupTitle(field),
          fields: [field],
          isCustom: field.is_custom_group,
          order: field.group_order,
        });
      }

      return groupList;
    }, new Array<FieldGroup>());

  return groups.sort((a, b) => a.order - b.order);
};
